import React, { useState, useCallback } from "react";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../Utils/axiosInstance";
import { toast } from "react-hot-toast";
import { setClearCart } from "../Redux/Slice/cart.slice";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { decryptData } from "../Utils/encryption";
import { CircularProgress } from "@mui/material";

// Stripe Element Styling - Dark Theme
const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '14px',
            color: '#ffffff',
            backgroundColor: 'transparent',
            '::placeholder': {
                color: 'rgba(255, 255, 255, 0.4)',
            },
            iconColor: 'rgba(255, 255, 255, 0.6)',
            // cursor: 'none',
        },
        invalid: {
            color: '#ef4444',
            iconColor: '#ef4444',
            // cursor: 'none',
        },
        complete: {
            color: '#10b981',
            iconColor: '#10b981',
            // cursor: 'none',
        },
    },
};

export default function StripeCheckoutForm({ clientSecret, orderData, onSuccess, onCancel, buyNow = false }) {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [cardholderName, setCardholderName] = useState("");
    const [cardErrors, setCardErrors] = useState({
        cardNumber: "",
        expiration: "",
        cvv: "",
        cardholderName: "",
    });
    const [isCardReady, setIsCardReady] = useState({
        cardNumber: false,
        expiry: false,
        cvc: false,
    });

    // Handle card element changes
    const handleCardNumberChange = useCallback((event) => {
        if (event.error) {
            setCardErrors(prev => ({ ...prev, cardNumber: event.error.message }));
        } else if (event.empty) {
            setCardErrors(prev => ({ ...prev, cardNumber: "Card number is required" }));
        } else {
            setCardErrors(prev => ({ ...prev, cardNumber: "" }));
        }
        setIsCardReady(prev => ({ ...prev, cardNumber: event.complete }));
    }, []);

    const handleCardExpiryChange = useCallback((event) => {
        if (event.error) {
            setCardErrors(prev => ({ ...prev, expiration: event.error.message }));
        } else if (event.empty) {
            setCardErrors(prev => ({ ...prev, expiration: "Expiry date is required" }));
        } else {
            setCardErrors(prev => ({ ...prev, expiration: "" }));
        }
        setIsCardReady(prev => ({ ...prev, expiry: event.complete }));
    }, []);

    const handleCardCvcChange = useCallback((event) => {
        if (event.error) {
            setCardErrors(prev => ({ ...prev, cvv: event.error.message }));
        } else if (event.empty) {
            setCardErrors(prev => ({ ...prev, cvv: "Security code is required" }));
        } else {
            setCardErrors(prev => ({ ...prev, cvv: "" }));
        }
        setIsCardReady(prev => ({ ...prev, cvc: event.complete }));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        // Validate cardholder name
        if (!cardholderName.trim()) {
            setCardErrors(prev => ({ ...prev, cardholderName: "Cardholder name is required" }));
            return;
        }

        setIsProcessing(true);
        setErrorMessage("");

        try {
            const cardNumberElement = elements.getElement(CardNumberElement);

            // Create payment method
            const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: cardholderName,
                    email: user?.email ? decryptData(user.email) : "",
                    address: {
                        country: "CA", // Canada as default
                    }
                },
            });

            if (paymentMethodError) {
                setErrorMessage(paymentMethodError.message);
                toast.error(paymentMethodError.message || "Payment failed");
                setIsProcessing(false);
                return;
            }

            // Confirm the payment
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (error) {
                setErrorMessage(error.message);
                toast.error(error.message || "Payment failed");
                setIsProcessing(false);
                return;
            }

            if (paymentIntent && paymentIntent.status === "succeeded") {
                // Verify payment with backend
                const response = await axiosInstance.post("/order/stripe/verify", {
                    items: orderData.items,
                    amount: orderData.amount,
                    paymentIntentId: paymentIntent.id,
                    buyNow: buyNow,
                    promoCode: orderData.promoCode || null,
                });

                if (response.data.success) {
                    // Only clear cart if it's not a buy now transaction
                    if (!buyNow) {
                        dispatch(setClearCart());
                    }
                    toast.success("Payment successful!");

                    // Generate invoice
                    try {
                        const updatedOrder = response.data.order;
                        const userDetails = {
                            name: user?.fullName ? decryptData(user.fullName) : "Customer",
                            email: user?.email ? decryptData(user.email) : "",
                        };
                        generateInvoice(updatedOrder, userDetails, 'save');
                    } catch (e) {
                        console.error("Error auto-opening invoice", e);
                    }

                    onSuccess();
                }
            }
        } catch (err) {
            console.error("Payment error:", err);
            setErrorMessage(err.response?.data?.error || "Payment processing failed");
            toast.error(err.response?.data?.error || "Payment processing failed");
        } finally {
            setIsProcessing(false);
        }
    };

    const isFormValid = cardholderName.trim() &&
        isCardReady.cardNumber &&
        isCardReady.expiry &&
        isCardReady.cvc;

    return (
        <form onSubmit={handleSubmit} className="space-y-6 cursor-none">
            {/* Loading Indicator */}
            {!stripe && (
                <div className="flex items-center justify-center py-8">
                    <div className="flex flex-col items-center gap-3">
                        <CircularProgress
                            size={40}
                            sx={{
                                color: "white",
                                '& .MuiCircularProgress-circle': { strokeLinecap: 'round' }
                            }}
                        />
                        <p className="text-white/60 text-sm">Loading payment fields...</p>
                    </div>
                </div>
            )}

            <div className={`space-y-4 ${!stripe ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'} transition-opacity duration-300`}>
                {/* Cardholder Name Field */}
                <div>
                    <label htmlFor="cardholderName" className="block text-sm font-medium text-white/80 mb-1 cursor-none">
                        Name on Card <span className="text-red-400 text-base cursor-none">*</span>
                    </label>
                    <input
                        id="cardholderName"
                        type="text"
                        value={cardholderName}
                        onChange={(e) => {
                            setCardholderName(e.target.value);
                            if (cardErrors.cardholderName) {
                                setCardErrors(prev => ({ ...prev, cardholderName: "" }));
                            }
                        }}
                        placeholder="John Doe"
                        className={`cursor-none w-full px-4 py-3 bg-white/5 border rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all ${cardErrors.cardholderName ? "border-red-400" : "border-white/10"
                            }`}
                    />
                    {cardErrors.cardholderName && (
                        <p className="text-xs text-red-400 mt-1">{cardErrors.cardholderName}</p>
                    )}
                </div>

                {/* Card Number */}
                <div>
                    <label className="block text-sm font-medium text-white/80 mb-1 cursor-none">
                        Debit & Credit Card Number <span className="text-red-400 text-base cursor-none">*</span>
                    </label>
                    <div
                        className={`cursor-none w-full px-4 py-3 bg-white/5 border rounded-lg transition-all ${cardErrors.cardNumber
                            ? "border-red-400"
                            : "border-white/10 focus-within:border-purple-500/50 focus-within:ring-2 focus-within:ring-purple-500/20"
                            }`}
                    >
                        <CardNumberElement
                            options={{
                                ...CARD_ELEMENT_OPTIONS,
                                showIcon: true,
                            }}
                            onChange={handleCardNumberChange}
                        />
                    </div>
                    {cardErrors.cardNumber && (
                        <p className="text-xs text-red-400 mt-1">{cardErrors.cardNumber}</p>
                    )}
                </div>

                {/* Expiry and CVC */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-1 cursor-none">
                            Expiry Date <span className="text-red-400 text-base cursor-none">*</span>
                        </label>
                        <div
                            className={`cursor-none w-full px-4 py-3 bg-white/5 border rounded-lg transition-all ${cardErrors.expiration
                                ? "border-red-400"
                                : "border-white/10 focus-within:border-purple-500/50 focus-within:ring-2 focus-within:ring-purple-500/20"
                                }`}
                        >
                            <CardExpiryElement
                                options={CARD_ELEMENT_OPTIONS}
                                onChange={handleCardExpiryChange}

                            />
                        </div>
                        {cardErrors.expiration && (
                            <p className="text-xs text-red-400 mt-1 cursor-none">{cardErrors.expiration}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-1 cursor-none">
                            Security Code <span className="text-red-400 text-base cursor-none">*</span>
                        </label>
                        <div
                            className={`cursor-none w-full px-4 py-3 bg-white/5 border rounded-lg transition-all ${cardErrors.cvv
                                ? "border-red-400"
                                : "border-white/10 focus-within:border-purple-500/50 focus-within:ring-2 focus-within:ring-purple-500/20"
                                }`}
                        >
                            <CardCvcElement
                                options={CARD_ELEMENT_OPTIONS}
                                onChange={handleCardCvcChange}
                            />
                        </div>
                        {cardErrors.cvv && (
                            <p className="text-xs text-red-400 mt-1 cursor-none">{cardErrors.cvv}</p>
                        )}
                    </div>
                </div>
            </div>

            {errorMessage && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                    {errorMessage}
                </div>
            )}

            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isProcessing}
                    className="cursor-none flex-1 py-3 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={!stripe || isProcessing || !isFormValid}
                    className="cursor-none flex-1 py-3 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {isProcessing ? (
                        <>
                            <CircularProgress size={20} sx={{ color: "white" }} />
                            <span>Processing...</span>
                        </>
                    ) : (
                        `Pay $${orderData?.amount?.toFixed(2) || "0.00"}`
                    )}
                </button>
            </div>

            <div className="text-center text-white/60 text-xs cursor-none">
                <p>🔒 Secure payment powered by Stripe</p>
            </div>
        </form>
    );
}